<template>
  <div :class="`${el}__moduleOverview`">
    <InViewAnimation v-if="$matchMedia.l" :delay="contentIdx * 4">
      <Headline slot="1" size="xs" :staticClass="`${el}__moduleSubline`">
        {{ content().headline }}
      </Headline>
    </InViewAnimation>
    <Row>
      <Col :xs="12" :l="4" :class="`${el}__moduleCol`">
        <div :class="`${el}__moduleOverviewDescription`">
          <InViewAnimation v-if="show" :delay="contentIdx * 4">
            <Divider e v-if="$matchMedia.l" class="u-margin__bottom--2" slot="1" dotted />
            <Copy element="div" slot="2" size="m" :centered="!$matchMedia.l" bold>
              {{ content().copy }}
            </Copy>
            <ul slot="3" :class="`${el}__moduleList`">
              <li v-for="(item, idx) in content().list" :key="`moduleOverviewList-${idx}`">
                {{ item }}
                <Link
                  v-if="idx === 0 && currentModule + 1 !== 5"
                  weight="regular"
                  @click="openContentSheet"
                  :onClick="
                    () =>
                      $emit(
                        'onTrackingClick',
                        '_click-' + $t('careLevelCalculator.wizard.moduleOverview.example'),
                        'element'
                      )
                  "
                >
                  {{ $t('careLevelCalculator.wizard.moduleOverview.example') }}
                </Link>
              </li>
            </ul>
            <Illustration
              v-if="!!content().illu"
              slot="4"
              class="u-overflow--hidden"
              brushColor="primaryBlue"
              :name="content().illu"
              :brush="currentModule + 1"
              :illuScale="1.8"
              responsive
              noLazy
            />
          </InViewAnimation>
        </div>
      </Col>
      <Col :xs="12" :l="8" :class="`${el}__moduleCol`">
        <InViewAnimation :delay="contentIdx + !$matchMedia.l ? 4 : 0" v-if="show">
          <template v-if="!$matchMedia.l">
            <Divider slot="1" />
            <Copy
              slot="1"
              element="div"
              size="xl"
              color="primaryBlue"
              bold
              :staticClass="`${el}__moduleSubline`"
            >
              {{ content().headline }}
            </Copy>
          </template>
          <div
            v-for="(item, index) in content().radiobuttons"
            :key="`moduleOverviewKey-${index}`"
            :slot="index + 2"
            :class="`${el}__moduleRadioWrapper`"
          >
            <template
              v-if="
                index > 0 &&
                hasInputCounter &&
                overViewRadioButtons[currentModule] &&
                overViewRadioButtons[currentModule][contentIdx] === index &&
                !$matchMedia.m
              "
            >
              <InputCounter
                class="u-margin__top--1"
                :defaultValue="activeCounterOverviewInputValue[contentIdx] || 1"
                @change="onInputCounterChange"
              />
            </template>
            <BaseRadio
              :id="`moduleOverview-${contentIdx}-${currentModule}-${index}`"
              :name="`moduleOverview-${contentIdx}-${currentModule}`"
              :label="`${item.label}`"
              :subLabel="item.subLabel"
              checkmark
              advanced
              :hasMargin="index > 0"
              :checked="
                overViewRadioButtons[currentModule] &&
                overViewRadioButtons[currentModule][contentIdx] === index
              "
              @input="onClick(index)"
              :tabindex="0"
              @keyup.enter.native="onClick(index)"
              @keyup.space.native="onClick(index)"
              role="radio"
              :aria-checked="
                overViewRadioButtons[currentModule] &&
                overViewRadioButtons[currentModule][contentIdx] === index
              "
            />
            <template
              v-if="
                index > 0 &&
                hasInputCounter &&
                overViewRadioButtons[currentModule] &&
                overViewRadioButtons[currentModule][contentIdx] === index &&
                $matchMedia.m
              "
            >
              <InputCounter
                :class="`${el}__moduleInputCounter`"
                :defaultValue="activeCounterOverviewInputValue[contentIdx] || 1"
                background="transparent"
                @change="onInputCounterChange"
              />
            </template>
          </div>
        </InViewAnimation>
      </Col>
    </Row>
  </div>
</template>

<script>
import Copy from '@/objects/Copy';
import Link from '@/objects/Link';
import Headline from '@/objects/Headline';
import Divider from '@/objects/Divider';
import InputCounter from '@/objects/InputCounter';
import BaseRadio from '@/objects/Forms/BaseRadio';
import Illustration from '@/objects/Illustration';
import InViewAnimation from '@/components/InViewAnimation';
import Row from '@/components/Grid/Row';
import Col from '@/components/Grid/Col';

import {
  CARELEVEL_UPDATE_OVERVIEW_INPUT_VALUE,
  CARELEVEL_UPDATE_OVERVIEW_RADIO_BUTTONS,
} from '@/store/actions/careLevelCalculator';

import { mapFieldsCareLevelCalculator } from '@/store/mapFields';
import { CONTENT_SHEET_CONTENT } from '@/store/actions/contentSheet';

export default {
  name: 'ModuleOverview',
  props: {
    el: {
      type: String,
    },
    currentModule: {
      type: Number,
      required: true,
    },
    headline: {
      type: String,
      required: true,
    },
    hasInputCounter: {
      type: Boolean,
      default: false,
    },
    contentIdx: {
      type: Number,
      default: 0,
    },
  },
  components: {
    Copy,
    Link,
    Headline,
    Divider,
    InputCounter,
    BaseRadio,
    Illustration,
    InViewAnimation,
    Row,
    Col,
  },
  data() {
    return {
      show: false,
      input: null,
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  created() {
    this.show = true;
  },
  computed: {
    ...mapFieldsCareLevelCalculator(['overViewRadioButtons', 'activeCounterOverviewInputValue']),
  },
  watch: {
    $route() {
      this.show = false;
      setTimeout(() => {
        this.show = true;
      }, 1);
    },
  },
  methods: {
    content() {
      return this.$t(`careLevelCalculator.wizard.moduleOverview.${this.currentModule + 1}`)[
        this.contentIdx
      ];
    },

    onInputCounterChange(value) {
      this.$store.dispatch(CARELEVEL_UPDATE_OVERVIEW_INPUT_VALUE, {
        contentIdx: this.contentIdx,
        value,
      });
    },

    onClick(value) {
      this.$store.dispatch(CARELEVEL_UPDATE_OVERVIEW_RADIO_BUTTONS, {
        currentModule: this.currentModule,
        contentIdx: this.contentIdx,
        value,
      });
      this.onInputCounterChange(1);
    },

    openContentSheet() {
      this.$store.dispatch(CONTENT_SHEET_CONTENT, {
        sheetId: `careLevelCalculator/Module${this.currentModule + 1}Example`,
      });
    },
  },
};
</script>
